import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { api } from '../services/api';
import {
  Target,
  Plus,
  Trash2,
  Edit3,
  Save,
  Award,
  Clock,
  Sparkles,
  CheckCircle,
  Mail,
  Shield,
  Check
} from 'lucide-react';

const SKILL_CATEGORIES = [
  'Frontend',
  'Backend',
  'AI/ML',
  'Mobile',
  'Design',
  'Database',
  'Infrastructure',
  'Business'
];

const PROFICIENCY_LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
const AVAILABILITY_OPTIONS = ['Available', 'Partially Available', 'Not Available'];

const AVATAR_PRESETS = [
  { id: 'dev-1', label: 'Tech Lead', bg: 'from-blue-600 to-indigo-600', emoji: '👨‍💻' },
  { id: 'dev-2', label: 'Frontend Dev', bg: 'from-emerald-500 to-teal-600', emoji: '👩‍💻' },
  { id: 'dev-3', label: 'AI Engineer', bg: 'from-purple-600 to-pink-600', emoji: '🤖' },
  { id: 'dev-4', label: 'Cloud Architect', bg: 'from-amber-500 to-orange-600', emoji: '☁️' },
  { id: 'dev-5', label: 'UI/UX Designer', bg: 'from-rose-500 to-red-600', emoji: '🎨' },
  { id: 'dev-6', label: 'Product Manager', bg: 'from-cyan-500 to-blue-600', emoji: '🚀' },
];

const Profile = () => {
  const { isDarkMode } = useTheme();
  const { currentUser, updateUserProfile, isAdmin, openOnboarding } = useAuth();

  const [profileData, setProfileData] = useState({
    name: '',
    title: '',
    bio: '',
    location: '',
    availability: 'Available',
    experienceYears: 8,
    avatar: 'dev-1',
    avatarUrl: '',
    skills: [],
    certifications: []
  });

  const [availableSkills, setAvailableSkills] = useState([]);

  useEffect(() => {
    let isMounted = true;
    api.getAllUniqueSkills().then(skills => {
      if (isMounted && Array.isArray(skills) && skills.length > 0) {
        setAvailableSkills(skills);
      }
    }).catch(err => {
      console.warn('Failed to pre-load skills in profile:', err);
    });
    return () => { isMounted = false; };
  }, []);

  const [newSkill, setNewSkill] = useState({
    name: '',
    level: 'Intermediate',
    category: 'Frontend',
    experienceYears: 1
  });

  const [newCert, setNewCert] = useState({
    name: '',
    issuer: '',
    year: new Date().getFullYear().toString(),
    credentialUrl: ''
  });

  const [isEditingBio, setIsEditingBio] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const syncToPeopleDirectory = useCallback(async (data) => {
    try {
      const savedPeople = JSON.parse(localStorage.getItem('skillmesh_people') || '[]');
      const userEmail = currentUser?.email || 'user@skillmesh.io';
      const existingIdx = savedPeople.findIndex(p => p.email === userEmail);
      const existingPerson = existingIdx >= 0 ? savedPeople[existingIdx] : null;

      const personId = existingPerson?.id || (currentUser?.id || `p_${Date.now()}`);
      const maxExp = (data.skills || []).reduce((acc, curr) => Math.max(acc, Number(curr.experienceYears) || 0), 0);
      const expNumber = data.experienceYears !== undefined && data.experienceYears !== ''
        ? Number(data.experienceYears)
        : Math.max(maxExp, 1);

      const personEntry = {
        id: personId,
        name: data.name,
        role: data.title || 'Platform Member',
        team: data.skills.length > 0 ? data.skills[0].category : (existingPerson?.team || 'Engineering'),
        email: userEmail,
        location: data.location || '',
        availability: data.availability,
        skills: data.skills.map(s => s.name),
        detailedSkills: data.skills,
        rating: existingPerson?.rating || 5.0,
        experience: `${expNumber} years`,
        bio: data.bio || '',
        avatar: data.avatarUrl || data.avatar || 'dev-1',
        certifications: data.certifications || []
      };

      if (existingIdx >= 0) {
        savedPeople[existingIdx] = personEntry;
      } else {
        savedPeople.push(personEntry);
      }
      localStorage.setItem('skillmesh_people', JSON.stringify(savedPeople));

      // Push to server Neo4j database directly so /people and /database reflect immediately
      await api.createPerson(personEntry);
    } catch (err) {
      console.error('Error syncing person directory:', err);
    }
  }, [currentUser]);

  // Sync profileData with currentUser and live directory
  useEffect(() => {
    if (currentUser) {
      const savedPeople = JSON.parse(localStorage.getItem('skillmesh_people') || '[]');
      const userEmail = (currentUser.email || '').toLowerCase();
      const existingPerson = savedPeople.find(p => (p.email || '').toLowerCase() === userEmail);
      let exp = currentUser.experienceYears;
      if (exp === undefined && existingPerson?.experience) {
        const parsed = parseInt(existingPerson.experience, 10);
        if (!isNaN(parsed)) exp = parsed;
      }
      const maxSkillExp = (currentUser.skills || []).reduce((acc, curr) => Math.max(acc, Number(curr.experienceYears) || 0), 0);
      if (maxSkillExp > (Number(exp) || 0)) {
        exp = maxSkillExp;
      }

      const initialData = {
        name: currentUser.name || (existingPerson?.name || (currentUser.email ? currentUser.email.split('@')[0] : '')),
        title: currentUser.title || (existingPerson?.role || 'SkillMesh Member'),
        bio: currentUser.bio || (existingPerson?.bio || ''),
        location: currentUser.location || (existingPerson?.location || 'Bucharest, Romania'),
        availability: currentUser.availability || (existingPerson?.availability || 'Available'),
        experienceYears: exp !== undefined && !isNaN(exp) ? exp : 0,
        avatar: currentUser.avatar || (existingPerson?.avatar || 'dev-1'),
        avatarUrl: currentUser.avatarUrl || '',
        skills: currentUser.skills || (existingPerson?.detailedSkills || []),
        certifications: currentUser.certifications || (existingPerson?.certifications || [])
      };

      setProfileData(initialData);

      // Auto-sync profile to Neo4j database & directory on mount
      syncToPeopleDirectory(initialData);
    }
  }, [currentUser, syncToPeopleDirectory]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddSkill = (e) => {
    e.preventDefault();
    if (!newSkill.name.trim()) return;

    // Check if skill already exists
    if (profileData.skills.some(s => s.name.toLowerCase() === newSkill.name.trim().toLowerCase())) {
      alert('Skill already added to your profile.');
      return;
    }

    const skillExp = Number(newSkill.experienceYears) || 1;
    const updatedSkills = [
      ...profileData.skills,
      {
        name: newSkill.name.trim(),
        level: newSkill.level,
        category: newSkill.category,
        experienceYears: skillExp
      }
    ];

    // Automatically increase overall experience if added skill experience is higher!
    const currentOverall = Number(profileData.experienceYears) || 0;
    const newOverallExp = Math.max(currentOverall, skillExp);

    const updatedProfile = { 
      ...profileData, 
      skills: updatedSkills,
      experienceYears: newOverallExp
    };
    setProfileData(updatedProfile);
    updateUserProfile({ skills: updatedSkills, experienceYears: newOverallExp });

    // Sync to people directory and push to Neo4j
    syncToPeopleDirectory(updatedProfile);

    setNewSkill({
      name: '',
      level: 'Intermediate',
      category: 'Frontend',
      experienceYears: 1
    });

    triggerSaveNotice();
  };

  const handleRemoveSkill = (skillNameToRemove) => {
    const target = String(skillNameToRemove || '').trim().toLowerCase();
    const updatedSkills = profileData.skills.filter(s => (s.name || '').trim().toLowerCase() !== target);
    const updatedProfile = { ...profileData, skills: updatedSkills };
    setProfileData(updatedProfile);
    updateUserProfile({ skills: updatedSkills });
    syncToPeopleDirectory(updatedProfile);
    triggerSaveNotice();
  };

  const handleSkillNameInputChange = (val) => {
    const matched = availableSkills.find(s => s.name.toLowerCase() === val.trim().toLowerCase());
    setNewSkill(prev => ({
      ...prev,
      name: val,
      category: matched?.category || prev.category,
      level: matched?.level || prev.level
    }));
  };

  const handleQuickAddExistingSkill = (skillObj) => {
    const isAlreadyAdded = profileData.skills.some(
      s => s.name.toLowerCase() === skillObj.name.toLowerCase()
    );
    if (isAlreadyAdded) {
      handleRemoveSkill(skillObj.name);
    } else {
      const skillExp = 2;
      const updatedSkills = [
        ...profileData.skills,
        {
          name: skillObj.name,
          level: skillObj.level || 'Intermediate',
          category: skillObj.category || 'General',
          experienceYears: skillExp
        }
      ];
      const currentOverall = Number(profileData.experienceYears) || 0;
      const newOverallExp = Math.max(currentOverall, skillExp);
      const updatedProfile = {
        ...profileData,
        skills: updatedSkills,
        experienceYears: newOverallExp
      };
      setProfileData(updatedProfile);
      updateUserProfile({ skills: updatedSkills, experienceYears: newOverallExp });
      syncToPeopleDirectory(updatedProfile);
      triggerSaveNotice();
    }
  };

  const handleAddCertification = (e) => {
    e.preventDefault();
    if (!newCert.name.trim()) return;

    if (profileData.certifications.some(c => c.name.toLowerCase() === newCert.name.trim().toLowerCase())) {
      alert('Certification already listed.');
      return;
    }

    const updatedCerts = [
      ...profileData.certifications,
      {
        name: newCert.name.trim(),
        issuer: newCert.issuer.trim() || 'Accredited Organization',
        year: newCert.year.trim() || new Date().getFullYear().toString(),
        credentialUrl: newCert.credentialUrl.trim()
      }
    ];

    const updatedProfile = { ...profileData, certifications: updatedCerts };
    setProfileData(updatedProfile);
    updateUserProfile({ certifications: updatedCerts });
    syncToPeopleDirectory(updatedProfile);

    setNewCert({
      name: '',
      issuer: '',
      year: new Date().getFullYear().toString(),
      credentialUrl: ''
    });

    triggerSaveNotice();
  };

  const handleRemoveCertification = (certNameToRemove) => {
    const updatedCerts = profileData.certifications.filter(c => c.name !== certNameToRemove);
    const updatedProfile = { ...profileData, certifications: updatedCerts };
    setProfileData(updatedProfile);
    updateUserProfile({ certifications: updatedCerts });
    syncToPeopleDirectory(updatedProfile);
    triggerSaveNotice();
  };

  const handleSaveProfile = (e) => {
    if (e) e.preventDefault();
    updateUserProfile(profileData);
    syncToPeopleDirectory(profileData);
    setIsEditingBio(false);
    triggerSaveNotice();
  };

  const triggerSaveNotice = () => {
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2500);
  };

  const getLevelBadgeClass = (level) => {
    switch (level) {
      case 'Expert': return isDarkMode ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' : 'bg-purple-50 text-purple-800 border-purple-300 font-semibold';
      case 'Advanced': return isDarkMode ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-blue-50 text-blue-800 border-blue-300 font-semibold';
      case 'Intermediate': return isDarkMode ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-green-50 text-green-800 border-green-300 font-semibold';
      default: return isDarkMode ? 'bg-amber-500/20 text-amber-300 border-amber-500/30' : 'bg-amber-100 text-amber-950 border-amber-300 font-bold';
    }
  };

  const completenessScore = (() => {
    let score = 0;
    if (profileData.name) score += 20;
    if (profileData.bio) score += 20;
    if (profileData.skills && profileData.skills.length > 0) score += 30;
    if (profileData.certifications && profileData.certifications.length > 0) score += 15;
    if (profileData.avatar || profileData.avatarUrl) score += 15;
    return Math.min(score, 100);
  })();

  const currentPreset = AVATAR_PRESETS.find(p => p.id === profileData.avatar) || AVATAR_PRESETS[0];

  return (
    <div className={`max-w-4xl mx-auto space-y-8 transition-colors duration-200 ${
      isDarkMode ? 'text-white' : 'text-light-950'
    }`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center space-x-2">
            <h1 className="text-3xl font-bold">My Profile & Defined Skills</h1>
            {isAdmin && (
              <span className="bg-purple-500/20 text-purple-400 border border-purple-500/30 text-xs px-2 py-0.5 rounded font-bold flex items-center space-x-1">
                <Shield className="h-3 w-3" />
                <span>Admin</span>
              </span>
            )}
          </div>
          <p className={`mt-1 transition-colors duration-200 ${
            isDarkMode ? 'text-dark-300' : 'text-light-600'
          }`}>
            Define your personal skills, experience, and certifications for team collaborations
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={openOnboarding}
            className="btn-secondary py-2 px-3.5 text-xs flex items-center space-x-1.5 shadow-sm"
            title="Launch interactive setup wizard"
          >
            <Sparkles className="h-4 w-4 text-primary-400" />
            <span>Setup Wizard</span>
          </button>

          {saveSuccess && (
            <div className="flex items-center space-x-2 px-3 py-1.5 rounded-lg bg-green-500/20 border border-green-500/30 text-green-400 text-sm animate-pulse">
              <CheckCircle className="h-4 w-4" />
              <span>Profile Saved!</span>
            </div>
          )}
        </div>
      </div>

      {/* Profile Completeness Banner */}
      <div className={`p-4 rounded-2xl border transition-all ${
        completenessScore === 100
          ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
          : isDarkMode ? 'bg-dark-900 border-dark-800' : 'bg-white border-light-200 shadow-sm'
      }`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <Sparkles className="h-4 w-4 text-primary-400" />
            <span className="font-bold text-sm">Profile Completeness: {completenessScore}%</span>
          </div>
          <span className="text-xs text-gray-400">
            {completenessScore === 100 ? 'All sections fully completed!' : 'Add skills, certifications, and bio for 100%'}
          </span>
        </div>
        <div className="w-full bg-dark-800 rounded-full h-2 overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${
              completenessScore === 100 ? 'bg-emerald-400' : 'bg-gradient-primary'
            }`}
            style={{ width: `${completenessScore}%` }}
          />
        </div>
      </div>

      {/* Basic Profile Card */}
      <div className={`card transition-all duration-200 ${
        isDarkMode ? 'dark' : 'light'
      }`}>
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center space-x-4">
            {profileData.avatarUrl ? (
              <img
                src={profileData.avatarUrl}
                alt={profileData.name}
                className="w-16 h-16 rounded-full object-cover shadow-lg border-2 border-primary-500"
              />
            ) : (
              <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${currentPreset.bg} flex items-center justify-center text-3xl shadow-lg border-2 border-primary-500/30`}>
                {currentPreset.emoji}
              </div>
            )}
            <div>
              <h2 className="text-2xl font-bold">{profileData.name || 'Anonymous Builder'}</h2>
              <p className="text-primary-400 text-sm font-medium">{profileData.title}</p>
              <div className="flex items-center flex-wrap gap-3 text-xs text-gray-400 mt-1">
                <span className="flex items-center space-x-1">
                  <Mail className="h-3.5 w-3.5" />
                  <span>{currentUser?.email || 'user@skillmesh.io'}</span>
                </span>
                {profileData.location && (
                  <span>• {profileData.location}</span>
                )}
              </div>
            </div>
          </div>

          <button
            onClick={() => setIsEditingBio(!isEditingBio)}
            className={`btn-secondary text-sm flex items-center space-x-1.5 ${
              isDarkMode ? 'dark' : 'light'
            }`}
          >
            <Edit3 className="h-4 w-4" />
            <span>{isEditingBio ? 'Cancel' : 'Edit Info'}</span>
          </button>
        </div>

        {isEditingBio ? (
          <form onSubmit={handleSaveProfile} className="space-y-4 pt-4 border-t border-gray-700/50">
            {/* Avatar Selector */}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-2 text-gray-400">
                Choose Avatar Preset
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
                {AVATAR_PRESETS.map((p) => {
                  const isSelected = profileData.avatar === p.id && !profileData.avatarUrl;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setProfileData(prev => ({ ...prev, avatar: p.id, avatarUrl: '' }))}
                      className={`flex flex-col items-center p-2 rounded-xl border transition-all ${
                        isSelected
                          ? 'border-primary-500 bg-primary-500/10 ring-2 ring-primary-500/50'
                          : isDarkMode ? 'border-dark-700 bg-dark-800' : 'border-light-200 bg-light-100'
                      }`}
                    >
                      <span className="text-xl">{p.emoji}</span>
                      <span className="text-[10px] mt-1 font-medium">{p.label}</span>
                    </button>
                  );
                })}
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-1">Or Photo URL</label>
                <input
                  type="url"
                  name="avatarUrl"
                  value={profileData.avatarUrl}
                  onChange={handleProfileChange}
                  placeholder="https://example.com/photo.jpg"
                  className={`input-field w-full text-xs ${isDarkMode ? 'dark' : 'light'}`}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Full Name</label>
                <input
                  type="text"
                  name="name"
                  value={profileData.name}
                  onChange={handleProfileChange}
                  className={`input-field w-full ${isDarkMode ? 'dark' : 'light'}`}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Title / Tagline</label>
                <input
                  type="text"
                  name="title"
                  value={profileData.title}
                  onChange={handleProfileChange}
                  placeholder="e.g. Full-Stack Developer, AI Researcher"
                  className={`input-field w-full ${isDarkMode ? 'dark' : 'light'}`}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Availability</label>
                <select
                  name="availability"
                  value={profileData.availability}
                  onChange={handleProfileChange}
                  className={`input-field w-full ${isDarkMode ? 'dark' : 'light'}`}
                >
                  {AVAILABILITY_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Location / City</label>
                <input
                  type="text"
                  name="location"
                  value={profileData.location}
                  onChange={handleProfileChange}
                  placeholder="e.g. Bucharest, Romania"
                  className={`input-field w-full ${isDarkMode ? 'dark' : 'light'}`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 flex items-center justify-between">
                  <span>Experience (Years)</span>
                </label>
                <input
                  type="number"
                  name="experienceYears"
                  min="0"
                  max="60"
                  value={profileData.experienceYears}
                  onChange={handleProfileChange}
                  className={`input-field w-full ${isDarkMode ? 'dark' : 'light'}`}
                  placeholder="e.g. 3"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Bio / Background</label>
              <textarea
                name="bio"
                value={profileData.bio}
                onChange={handleProfileChange}
                rows="3"
                className={`input-field w-full ${isDarkMode ? 'dark' : 'light'}`}
                placeholder="Share your professional background, project interests, or experience..."
              />
            </div>

            <button type="submit" className="btn-primary flex items-center space-x-2 text-sm py-2">
              <Save className="h-4 w-4" />
              <span>Save Basic Info</span>
            </button>
          </form>
        ) : (
          <div className="space-y-4 pt-4 border-t border-gray-700/30">
            <p className={`text-sm ${isDarkMode ? 'text-dark-300' : 'text-light-600'}`}>
              {profileData.bio}
            </p>
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div className="flex items-center space-x-2">
                <span className="font-semibold text-xs uppercase tracking-wider text-gray-400">Status:</span>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                  profileData.availability === 'Available' ? (isDarkMode ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-emerald-50 text-emerald-800 border border-emerald-300 font-bold') :
                  profileData.availability === 'Partially Available' ? (isDarkMode ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-amber-100 text-amber-950 border border-amber-300 font-bold') :
                  (isDarkMode ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-red-50 text-red-800 border border-red-300 font-bold')
                }`}>
                  {profileData.availability}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="font-semibold text-xs uppercase tracking-wider text-gray-400">Experience:</span>
                <span className="font-bold text-sm text-primary-400">
                  {profileData.experienceYears || 1} years
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Define Skills Section */}
      <div className={`card transition-all duration-200 ${
        isDarkMode ? 'dark' : 'light'
      }`}>
        <h2 className="text-xl font-bold mb-4 flex items-center space-x-2">
          <Target className="h-5 w-5 text-primary-400" />
          <span>Define Your Skills</span>
        </h2>
        <p className={`text-sm mb-4 ${isDarkMode ? 'text-dark-300' : 'text-light-600'}`}>
          Add skills you possess along with your self-reported proficiency level and category so project creators and matching algorithms can find you.
        </p>

        {/* Pre-loaded Existing Skills (1-Click Quick Select) */}
        {availableSkills.length > 0 && (
          <div className={`p-4 rounded-xl mb-6 border ${
            isDarkMode ? 'bg-dark-800/60 border-dark-700' : 'bg-light-100 border-light-200'
          }`}>
            <div className="flex items-center justify-between mb-2.5">
              <label className="text-xs font-semibold uppercase tracking-wider flex items-center space-x-1.5 text-primary-400">
                <Sparkles className="h-3.5 w-3.5" />
                <span>Pre-loaded Organization Skills (Click to Quick-Add / Toggle)</span>
              </label>
              <span className="text-[11px] text-gray-400">
                {profileData.skills.length} on profile
              </span>
            </div>
            <div className="flex flex-wrap gap-2 max-h-44 overflow-y-auto pr-1">
              {availableSkills.map((sk) => {
                const isAdded = profileData.skills.some(
                  s => s.name.toLowerCase() === sk.name.toLowerCase()
                );
                return (
                  <button
                    key={sk.name}
                    type="button"
                    onClick={() => handleQuickAddExistingSkill(sk)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border flex items-center space-x-1.5 transition-all ${
                      isAdded
                        ? 'bg-primary-600 text-white border-primary-500 shadow-md font-bold'
                        : isDarkMode
                          ? 'bg-dark-800 text-dark-300 border-dark-700 hover:border-primary-500 hover:text-white'
                          : 'bg-white text-light-700 border-light-300 hover:border-primary-500 hover:text-primary-600 shadow-sm'
                    }`}
                    title={sk.category ? `Category: ${sk.category}` : undefined}
                  >
                    {isAdded ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                    <span>{sk.name}</span>
                    {sk.category && (
                      <span className={`text-[10px] opacity-75 px-1 py-0.2 rounded ${
                        isAdded ? 'bg-white/20' : isDarkMode ? 'bg-dark-700' : 'bg-light-200'
                      }`}>
                        {sk.category}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Form to Add New Skill */}
        <form onSubmit={handleAddSkill} className={`p-4 rounded-xl mb-6 border ${
          isDarkMode ? 'bg-dark-800/80 border-dark-700' : 'bg-light-100 border-light-200'
        }`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-xs font-semibold mb-1">Skill Name * <span className="text-primary-400 text-[11px] font-normal">(Autocomplete)</span></label>
              <input
                type="text"
                list="profile-skills-datalist"
                value={newSkill.name}
                onChange={(e) => handleSkillNameInputChange(e.target.value)}
                placeholder="e.g., React, PyTorch, Figma"
                className={`input-field w-full text-sm ${isDarkMode ? 'dark' : 'light'}`}
                required
              />
              <datalist id="profile-skills-datalist">
                {availableSkills.map((sk) => (
                  <option key={sk.name} value={sk.name}>
                    {sk.category ? `${sk.category} • ` : ''}{sk.level || 'Intermediate'}
                  </option>
                ))}
              </datalist>
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1">Category</label>
              <select
                value={newSkill.category}
                onChange={(e) => setNewSkill(prev => ({ ...prev, category: e.target.value }))}
                className={`input-field w-full text-sm ${isDarkMode ? 'dark' : 'light'}`}
              >
                {SKILL_CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1">Proficiency Level</label>
              <select
                value={newSkill.level}
                onChange={(e) => setNewSkill(prev => ({ ...prev, level: e.target.value }))}
                className={`input-field w-full text-sm ${isDarkMode ? 'dark' : 'light'}`}
              >
                {PROFICIENCY_LEVELS.map(lvl => (
                  <option key={lvl} value={lvl}>{lvl}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1">Experience (Years)</label>
              <input
                type="number"
                min="0"
                max="30"
                value={newSkill.experienceYears}
                onChange={(e) => setNewSkill(prev => ({ ...prev, experienceYears: e.target.value }))}
                className={`input-field w-full text-sm ${isDarkMode ? 'dark' : 'light'}`}
              />
            </div>
          </div>

          <button
            type="submit"
            className="btn-primary flex items-center space-x-1.5 text-sm py-2 px-4"
          >
            <Plus className="h-4 w-4" />
            <span>Add Skill to Profile</span>
          </button>
        </form>

        {/* Current Defined Skills Grid */}
        <div>
          <h3 className="text-base font-semibold mb-3">Your Defined Skills ({profileData.skills.length})</h3>

          {profileData.skills.length === 0 ? (
            <div className={`p-6 text-center rounded-lg border border-dashed ${
              isDarkMode ? 'border-dark-700 text-dark-300' : 'border-light-300 text-light-600'
            }`}>
              <Sparkles className="h-8 w-8 mx-auto mb-2 text-primary-400 opacity-60" />
              <p className="text-sm">No skills added yet. Use the form above to define your skill set!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {profileData.skills.map((skill, index) => (
                <div
                  key={index}
                  className={`p-3.5 rounded-lg border flex items-start justify-between transition-all hover:shadow-md ${
                    isDarkMode
                      ? 'bg-dark-800 border-dark-700 hover:border-dark-600'
                      : 'bg-white border-light-200 hover:border-light-300'
                  }`}
                >
                  <div className="space-y-1">
                    <div className="font-semibold text-sm flex items-center space-x-1.5">
                      <span>{skill.name}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-xs">
                      <span className={`px-2 py-0.5 rounded border font-medium text-[11px] ${getLevelBadgeClass(skill.level)}`}>
                        {skill.level}
                      </span>
                      <span className="text-gray-400">{skill.category}</span>
                    </div>
                    {skill.experienceYears > 0 && (
                      <div className="text-[11px] text-gray-400 flex items-center space-x-1">
                        <Clock className="h-3 w-3" />
                        <span>{skill.experienceYears} yr{skill.experienceYears > 1 ? 's' : ''} experience</span>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => handleRemoveSkill(skill.name)}
                    className="text-gray-400 hover:text-red-400 p-1 rounded hover:bg-red-500/10 transition-colors"
                    title="Remove Skill"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Certifications & Badges Section */}
      <div className={`card transition-all duration-200 ${
        isDarkMode ? 'dark' : 'light'
      }`}>
        <h2 className="text-xl font-bold mb-4 flex items-center space-x-2">
          <Award className="h-5 w-5 text-amber-400" />
          <span>Certifications & Badges ({profileData.certifications.length})</span>
        </h2>
        <p className={`text-sm mb-6 ${isDarkMode ? 'text-dark-300' : 'text-light-600'}`}>
          Showcase verified credentials (AWS, Google Cloud, Meta, Scrum, Kubernetes, etc.) to boost your credibility.
        </p>

        {/* Add Certification Form */}
        <form onSubmit={handleAddCertification} className={`p-4 rounded-xl mb-6 border ${
          isDarkMode ? 'bg-dark-800/80 border-dark-700' : 'bg-light-100 border-light-200'
        }`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold mb-1">Certification Name *</label>
              <input
                type="text"
                value={newCert.name}
                onChange={(e) => setNewCert(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. AWS Certified Solutions Architect"
                className={`input-field w-full text-sm ${isDarkMode ? 'dark' : 'light'}`}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1">Issuer / Organization</label>
              <input
                type="text"
                value={newCert.issuer}
                onChange={(e) => setNewCert(prev => ({ ...prev, issuer: e.target.value }))}
                placeholder="e.g. Amazon Web Services"
                className={`input-field w-full text-sm ${isDarkMode ? 'dark' : 'light'}`}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1">Issue Year</label>
              <input
                type="text"
                value={newCert.year}
                onChange={(e) => setNewCert(prev => ({ ...prev, year: e.target.value }))}
                placeholder="2024"
                className={`input-field w-full text-sm ${isDarkMode ? 'dark' : 'light'}`}
              />
            </div>
          </div>

          <button
            type="submit"
            className="btn-primary flex items-center space-x-1.5 text-sm py-2 px-4"
          >
            <Plus className="h-4 w-4" />
            <span>Add Certification</span>
          </button>
        </form>

        {/* List of Certifications */}
        <div>
          <h3 className="text-base font-semibold mb-3">Your Listed Credentials ({profileData.certifications.length})</h3>

          {profileData.certifications.length === 0 ? (
            <div className={`p-6 text-center rounded-lg border border-dashed ${
              isDarkMode ? 'border-dark-700 text-dark-300' : 'border-light-300 text-light-600'
            }`}>
              <Award className="h-8 w-8 mx-auto mb-2 text-amber-400 opacity-60" />
              <p className="text-sm">No certifications added yet. Use the form above to add your certifications!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {profileData.certifications.map((cert, index) => (
                <div
                  key={index}
                  className={`p-3.5 rounded-lg border flex items-start justify-between transition-all hover:shadow-md ${
                    isDarkMode
                      ? 'bg-dark-800 border-dark-700 hover:border-dark-600'
                      : 'bg-white border-light-200 hover:border-light-300'
                  }`}
                >
                  <div className="space-y-1">
                    <div className="font-semibold text-sm flex items-center space-x-1.5">
                      <Award className="h-4 w-4 text-amber-400 flex-shrink-0" />
                      <span className="line-clamp-1">{cert.name}</span>
                    </div>
                    <div className="text-xs text-gray-400">
                      {cert.issuer} • {cert.year}
                    </div>
                  </div>

                  <button
                    onClick={() => handleRemoveCertification(cert.name)}
                    className="text-gray-400 hover:text-red-400 p-1 rounded hover:bg-red-500/10 transition-colors"
                    title="Remove Certification"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
